import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";
import {
  HERO_DATA,
  HIGHLIGHTS,
  STORY_DATA,
  PRODUCTS,
  TEXTURE_TILES,
  PRODUCT_CATEGORIES,
  IMAGES,
} from "../constants/kalorenceData";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";

const FEATURED = [PRODUCTS[0], PRODUCTS[6], PRODUCTS[9], PRODUCTS[11]];

const CATEGORY_TILES = [
  { key: "dupattas", label: PRODUCT_CATEGORIES.DUPATTAS_STOLES, image: IMAGES.img_9 },
  { key: "totes", label: PRODUCT_CATEGORIES.TOTES, image: IMAGES.img_17 },
  { key: "apparel", label: PRODUCT_CATEGORIES.TEES, image: IMAGES.img_21 },
  { key: "bandanas", label: PRODUCT_CATEGORIES.BANDANAS, image: IMAGES.img_24 },
];

export const Home = ({ onAdd, onQuickView, setActiveTab, goShopCategory }) => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_DATA.bgImage}
            alt="Ivory ecoprinted dupatta"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2B3A2F]/85 via-[#2B3A2F]/55 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl text-[#F7F5F0]"
          >
            <p className="text-xs sm:text-sm tracking-[0.25em] uppercase font-semibold text-[#E2DACF] mb-6 flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#C47D65]" />
              {HERO_DATA.eyebrow}
            </p>
            <h1 className="font-cormorant text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight whitespace-pre-line">
              {HERO_DATA.title}
            </h1>
            <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-[#F7F5F0]/90 max-w-xl">
              {HERO_DATA.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-shop-button"
                onClick={() => {
                  setActiveTab("shop");
                  window.scrollTo({ top: 0 });
                }}
                className="bg-[#C47D65] text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b06752] transition-all flex items-center justify-center gap-2 group"
              >
                Explore the Collection
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-story-button"
                onClick={() => {
                  setActiveTab("story");
                  window.scrollTo({ top: 0 });
                }}
                className="border border-[#F7F5F0]/40 text-[#F7F5F0] px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#F7F5F0]/10 transition-all"
              >
                Our Craft
              </button>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#F7F5F0]/70 text-[10px] tracking-[0.3em] uppercase animate-pulse">
          Scroll
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-[#F7F5F0] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.12} testId={`highlight-${i}`}>
              <div className="text-center md:text-left space-y-3">
                <span className="text-4xl">{h.icon}</span>
                <h3 className="font-cormorant text-2xl font-normal pt-2">{h.title}</h3>
                <p className="text-sm text-[#5C6B5F] font-light leading-relaxed">{h.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-[#EDEAE2] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-12 max-w-xl">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                The Collections
              </p>
              <h2 className="font-cormorant text-4xl sm:text-5xl font-light mt-3">
                Four ways to carry the forest
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {CATEGORY_TILES.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.1}>
                <button
                  data-testid={`category-tile-${c.key}`}
                  onClick={() => goShopCategory(c.key)}
                  className="group relative w-full aspect-[3/4] overflow-hidden block text-left"
                >
                  <img
                    src={c.image}
                    alt={c.label}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2B3A2F]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <h3 className="font-cormorant text-xl sm:text-2xl text-[#F7F5F0] font-light leading-tight">
                      {c.label}
                    </h3>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#E2DACF] flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Shop <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STORY TEASER */}
      <section className="bg-[#F7F5F0] py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <img
                src={STORY_DATA.bgImage}
                alt="Botanicals on fabric"
                className="w-full aspect-[4/5] object-cover"
              />
              <img
                src={STORY_DATA.detailImage}
                alt="Golden linen ecoprint"
                className="hidden sm:block absolute -bottom-10 -right-8 w-44 h-56 object-cover border-8 border-[#F7F5F0] shadow-xl"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-6">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                {STORY_DATA.label}
              </p>
              <h2 className="font-cormorant text-4xl sm:text-5xl font-light leading-tight">
                {STORY_DATA.title}
              </h2>
              <p className="text-base text-[#5C6B5F] font-light leading-relaxed">
                {STORY_DATA.text1}
              </p>
              <blockquote className="border-l-2 border-[#C47D65] pl-5 font-cormorant italic text-xl sm:text-2xl text-[#2B3A2F] font-light">
                {STORY_DATA.quote}
              </blockquote>
              <button
                data-testid="story-teaser-button"
                onClick={() => {
                  setActiveTab("story");
                  window.scrollTo({ top: 0 });
                }}
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#2B3A2F] border-b border-[#2B3A2F] pb-1 hover:text-[#C47D65] hover:border-[#C47D65] transition-colors"
              >
                Read the full story <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="bg-[#EDEAE2] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
              <div className="max-w-xl">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                  Loved by Many
                </p>
                <h2 className="font-cormorant text-4xl sm:text-5xl font-light mt-3">
                  Bestselling botanicals
                </h2>
              </div>
              <button
                data-testid="bestseller-view-all"
                onClick={() => {
                  setActiveTab("shop");
                  window.scrollTo({ top: 0 });
                }}
                className="text-xs tracking-[0.2em] uppercase font-semibold flex items-center gap-2 hover:text-[#C47D65] transition-colors"
              >
                View all pieces <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
            {FEATURED.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <ProductCard product={p} onAdd={onAdd} onQuickView={onQuickView} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEXTURE TILES */}
      <section className="bg-[#2B3A2F] py-20 sm:py-28 text-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C47D65]">
                Up Close
              </p>
              <h2 className="font-cormorant text-4xl sm:text-5xl font-light mt-3">
                The texture of patience
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {TEXTURE_TILES.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="group relative overflow-hidden aspect-square">
                  <img
                    src={t.image}
                    alt={t.caption}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#2B3A2F]/0 group-hover:bg-[#2B3A2F]/70 transition-colors duration-500 flex items-end p-4">
                    <p className="text-xs font-light leading-snug opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {t.caption}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
